import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Factory, Wrench, Settings, Shield, CheckCircle } from 'lucide-react';
import { solutions, fireProtectionSystems } from '../mock';

const Solutions = () => {
  const icons = [Factory, Wrench, Settings, Shield];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }}></div>
        <div className="absolute left-0 top-0 h-1 w-full bg-red-600"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="eyebrow" style={{ color: '#f4a8b4' }}>Our Solutions</span>
            <h1 className="font-display text-4xl md:text-6xl font-extrabold mb-5 mt-3 text-balance">Comprehensive Fire & Safety Services</h1>
            <p className="text-base md:text-lg text-slate-300">
              End-to-end solutions from manufacturing to maintenance, ensuring your fire safety equipment performs at its best.
            </p>
          </div>
        </div>
      </section>

      {/* Main Solutions */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = icons[index];
              return (
                <Card key={solution.id} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="h-7 w-7 text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-semibold text-slate-900 mb-2">{solution.title}</h3>
                        <p className="text-slate-600 leading-relaxed">{solution.description}</p>
                      </div>
                    </div>
                    
                    <div className="mt-6 space-y-2">
                      <p className="text-sm font-semibold text-slate-700 mb-3">Key Capabilities:</p>
                      {solution.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fire Protection Services */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Fire Protection Systems Services</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Complete lifecycle support for all fire protection systems
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Design & Consultancy</h3>
                <p className="text-sm text-slate-600 mb-4">
                  End-to-end engineering solutions tailored to your facility's unique requirements
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Facility assessment</li>
                  <li>• System design</li>
                  <li>• Risk analysis</li>
                  <li>• Compliance planning</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">System Installation</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Professional installation by certified technicians ensuring optimal performance
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Expert installation</li>
                  <li>• Quality components</li>
                  <li>• Safety protocols</li>
                  <li>• Timely delivery</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Testing & Commissioning</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Comprehensive testing and inspection to ensure system reliability
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Performance testing</li>
                  <li>• System validation</li>
                  <li>• Documentation</li>
                  <li>• Training provided</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Fire Audit</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Detailed fire safety assessments to identify risks and ensure compliance
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Risk assessment</li>
                  <li>• Compliance check</li>
                  <li>• Audit reports</li>
                  <li>• Recommendations</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Retrofitting</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Modernization and upgradation of existing fire protection systems
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• System upgrades</li>
                  <li>• Technology integration</li>
                  <li>• Performance enhancement</li>
                  <li>• Cost-effective solutions</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">NFPA / NBC Compliance</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Expert assistance with international and national fire safety standards
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Standards compliance</li>
                  <li>• Documentation support</li>
                  <li>• Certification assistance</li>
                  <li>• Regulatory guidance</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Systems */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Fire Protection Systems We Service</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {fireProtectionSystems.map((system, index) => (
              <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">{system.category}</h3>
                  <ul className="space-y-2">
                    {system.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <div className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Partner with Industry Leaders
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-red-100">
            Let our experts help you design, implement, and maintain world-class fire safety solutions
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-red-600 hover:bg-slate-100 px-8 py-3 rounded-md font-medium transition-colors"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;